import { PaymentVerificationResponse, PurchaseDetails } from '../types/purchase';

import axiosInstance from './axiosInstance';

export interface CreditUsageRates {
  chat_message: number;
  document_page: number;
  design_editor_revision: number;
  editor_hour: number;
  developer_input_million_tokens: number;
  developer_output_million_tokens: number;
  design_section: number;
  deployment: number;
  compute_cpu_hour: number;
  compute_ram_gib_hour: number;
  compute_disk_gib_hour: number;
  task_fix?: number;
  custom_domain_reference_price_usd?: number;
  custom_domain_registration?: number;
}

/** Free plan monthly cycle: upfront grant, daily boost, then a daily tail; resets from the signup date. */
export interface FreeCreditSchedule {
  upfront: number;
  boost_days: number;
  boost_daily: number;
  daily: number;
  /** Days of the tail grant (after the boost days, up to day 30 of the cycle). */
  tail_days?: number;
  cycle_days: number;
  estimated_total: number;
  expires_each_cycle: boolean;
}

export interface PlanConfig {
  name: string;
  monthly_price: number;
  annual_price: number | null;
  /** Credits granted with each payment (half of the month for paid plans). */
  monthly_credits: number;
  /** Credits a full month gives: the payment grant plus the rest spread over the billing month. */
  monthly_total_credits?: number;
  /** A typical day's share of the spread (it varies slightly with the month's length). */
  daily_drip_credits?: number;
  free_credit_schedule?: FreeCreditSchedule;
  max_users: number;
  topup_price_per_credit: number | null;
  topup_price_per_credit_apple?: number | null;
  monthly_price_inr?: number;
  annual_price_inr?: number | null;
  topup_price_per_credit_inr?: number | null;
  /** Smallest credit top-up the plan sells (its price clears the card processors' minimum charge). */
  minimum_topup_credits?: number | null;
  apple_product_id_monthly?: string;
  apple_product_id_annual?: string;
  apple_price_monthly?: number;
  apple_price_annual?: number;
  features?: string[];
  credit_usage_rates?: CreditUsageRates;
  is_unlimited?: boolean;
  topups_allowed?: boolean;
  apple_available?: boolean;
  organization_unlimited?: boolean;
}

export interface UserSubscription {
  plan_id: string;
  plan_name: string;
  status: string;
  credits_balance: number;
  is_unlimited?: boolean;
  effective_credits_unlimited?: boolean;
  renews_at: string | null;
  plan_config: PlanConfig;
  subscription_provider?: string;  // 'apple' | 'stripe' | 'razorpay' | null
  project_credits?: ProjectCreditAllocation[];
  next_daily_credit_grant?: DailyCreditGrant | null;
}

export interface DailyCreditGrant {
  amount: number;
  scheduled_at: string;
  status: 'scheduled' | 'pending';
  cycle_reset?: boolean;
  plan_id: string;
  plan_name: string;
}

export interface CreditRefillStatus {
  plan_id: string;
  plan_name: string;
  timezone: string;
  daily_drip_credits: number;
  next_daily_credit_grant: DailyCreditGrant | null;
  next_daily_credit_grant_reason?: 'plan_not_eligible' | 'subscription_inactive'
    | 'renewal_required' | 'schedule_unavailable' | null;
}

export interface ProjectCreditAllocation {
  project_id: string;
  project_title: string;
  role: string;
  team_role: string;
  manager_user_id?: string | null;
  manager_name?: string | null;
  ai_credit_quota?: number | null;
  ai_credits_used: number;
  ai_credits_remaining?: number | null;
  ai_credit_quota_allocated: number;
  ai_credit_quota_exhausted: boolean;
  quota_required: boolean;
  is_unlimited?: boolean;
  unlimited_scope?: 'personal' | 'organization' | null;
}

export interface TopUpOrderResponse {
  order_id: string;
  amount: number;
  currency: string;
  key_id: string;
  payment_id: string;
  payment_provider?: string;        // 'razorpay' | 'stripe'
  stripe_checkout_url?: string;     // Only for Stripe
  session_id?: string;              // Stripe session ID
  prompt_credits?: number;
}

export interface SubscriptionResponse {
  subscription_id: string;
  key_id: string;
  plan_id: string;
  payment_provider?: string;        // 'razorpay' | 'stripe'
  stripe_checkout_url?: string;     // Only for Stripe
  session_id?: string;              // Stripe session ID
}

export interface CancellationSurveyPayload {
  reason: string;
  feedback: string;
  /** The save screen this cancellation went past, so we can measure saves. */
  save_id?: string | null;
}



export const subscriptionService = {
  getPlans: async (): Promise<Record<string, PlanConfig>> => {
    const response = await axiosInstance.get('/api/v1/subscription/plans');

    return response.data;
  },

  getCurrentSubscription: async (): Promise<UserSubscription> => {
    const response = await axiosInstance.get('/api/v1/subscription/current');

    return response.data;
  },

  getNextCreditGrant: async (signal?: globalThis.AbortSignal): Promise<CreditRefillStatus> => {
    const response = await axiosInstance.get('/api/v1/billing/next-credit-grant', { signal });

    return response.data;
  },

  createTopUpOrder: async (credits: number, currency: string = 'USD'): Promise<TopUpOrderResponse> => {
    const response = await axiosInstance.post('/api/v1/subscription/topup/order', { credits, currency });

    return response.data;
  },

  verifyPayment: async (paymentDetails: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }): Promise<PaymentVerificationResponse> => {
    const response = await axiosInstance.post('/api/v1/subscription/verify', paymentDetails);

    return response.data;
  },

  createSubscription: async (tier: string, interval: string, currency: string): Promise<SubscriptionResponse> => {
    const response = await axiosInstance.post('/api/v1/subscription/subscribe', { tier, interval, currency });

    return response.data;
  },

  verifySubscription: async (paymentDetails: {
    razorpay_payment_id: string;
    razorpay_subscription_id: string;
    razorpay_signature: string;
  }): Promise<PaymentVerificationResponse> => {
    const response = await axiosInstance.post('/api/v1/subscription/verify-subscription', paymentDetails);

    return response.data;
  },

  getPricingPageData: async (): Promise<{ plans: Record<string, PlanConfig>; currentSub: UserSubscription | null }> => {
    const [plans, currentSub] = await Promise.all([
      subscriptionService.getPlans(),
      subscriptionService.getCurrentSubscription().catch(() => null),
    ]);

    return { plans, currentSub };
  },

  cancelSubscription: async (survey: CancellationSurveyPayload): Promise<{ status: string; message: string }> => {
    const response = await axiosInstance.post('/api/v1/subscription/cancel', survey);

    return response.data;
  },

  verifyStripeSession: async (sessionId: string): Promise<{
    status: string;
    type: string;
    purchase?: PurchaseDetails;
  }> => {
    const response = await axiosInstance.post('/api/v1/subscription/verify-stripe-session', {
      session_id: sessionId,
    });

    return response.data;
  },

  retryStripePayment: async (paymentId: string): Promise<TopUpOrderResponse> => {
    const response = await axiosInstance.post('/api/v1/subscription/retry-stripe', {
      payment_id: paymentId,
    });

    return response.data;
  },
};
